import type {
  AdminPlatformData,
  AgencyApplication,
  AgencyEvent,
  EventRegistration,
  PublicPlatformData,
  Settings,
  TalentNotification,
  TalentProfile,
  User,
} from "../types";

export const PROJECT_ID =
  import.meta.env.VITE_BLISS_PROJECT_ID ||
  "58c56fae-591f-4cbe-a745-681c54da2949";
const APP_BASE = `/api/projects/${encodeURIComponent(PROJECT_ID)}/app`;
const TOKEN_KEY = "paris-runway-app-token";

export function getToken() {
  return localStorage.getItem(TOKEN_KEY) || "";
}

export function setToken(token: string) {
  if (token) localStorage.setItem(TOKEN_KEY, token);
  else localStorage.removeItem(TOKEN_KEY);
}

function message(payload: unknown, fallback: string) {
  const data = payload as {
    error?: string | { message?: string };
    message?: string;
  } | null;
  return typeof data?.error === "string"
    ? data.error
    : data?.error?.message || data?.message || fallback;
}

async function request<T>(
  path: string,
  init: RequestInit = {},
  auth = false,
): Promise<T> {
  const headers = new Headers(init.headers);
  if (!(init.body instanceof FormData))
    headers.set("Content-Type", "application/json");
  if (auth) {
    const token = getToken();
    if (token) headers.set("Authorization", `Bearer ${token}`);
  }
  const response = await fetch(`${APP_BASE}${path}`, { ...init, headers });
  const payload = await response.json().catch(() => null);
  if (!response.ok)
    throw new Error(message(payload, `Request failed (${response.status})`));
  return payload as T;
}

function data<T>(payload: { data?: T } | T): T {
  return ((payload as { data?: T }).data ?? payload) as T;
}

export const api = {
  async publicTalent(slug = "") {
    const payload = await request<{ data: PublicPlatformData }>(
      `/talent-platform${slug ? `?slug=${encodeURIComponent(slug)}` : ""}`,
    );
    return data(payload);
  },
  async applyOpportunity(body: Record<string, unknown>) {
    return data(
      await request<{ data: { application_id: string } }>("/talent-platform", {
        method: "POST",
        body: JSON.stringify({ action: "apply-opportunity", ...body }),
      }),
    );
  },
  async applyAgency(body: Record<string, unknown>) {
    return data(
      await request<{ data: { application_id: string } }>("/talent-platform", {
        method: "POST",
        body: JSON.stringify({ action: "apply-agency", ...body }),
      }),
    );
  },
  async agencyApplication(token: string) {
    return data(
      await request<{
        data: {
          application: AgencyApplication;
          pricing: { amount: number; currency: string };
          commerce_ready: boolean;
        };
      }>(`/talent-platform/agency-application?token=${encodeURIComponent(token)}`),
    );
  },
  async agencyApplicationAction(
    action: "start" | "confirm" | "activate",
    token: string,
    body: Record<string, unknown> = {},
  ) {
    const result = data(
      await request<{
        data: {
          url?: string;
          session_id?: string;
          paid?: boolean;
          status?: string;
          token?: string;
          user?: User;
        };
      }>("/talent-platform/agency-application", {
        method: "POST",
        body: JSON.stringify({ action, token, ...body }),
      }),
    );
    if (action === "activate" && result.token) setToken(result.token);
    return result;
  },
  async eventCheckout(action: string, body: Record<string, unknown> = {}) {
    return data(
      await request<{
        data: {
          url?: string;
          session_id?: string;
          paid?: boolean;
          status?: string;
        };
      }>("/talent-platform/event-checkout", {
        method: "POST",
        body: JSON.stringify({ action, ...body }),
      }),
    );
  },
  async login(email: string, password: string) {
    let payload: {
      token: string;
      user: User;
      data?: { token: string; user: User };
    };
    try {
      payload = await request("/auth/login", {
        method: "POST",
        body: JSON.stringify({ email, password }),
      });
    } catch {
      payload = await request("/talent-platform/admin-login", {
        method: "POST",
        body: JSON.stringify({ email, password }),
      });
    }
    const result = data(payload);
    setToken(result.token);
    return result.user;
  },
  async requestPasswordReset(email: string) {
    return data(
      await request<{ data: { message: string } }>(
        "/talent-platform/password-reset",
        {
          method: "POST",
          body: JSON.stringify({ email, resetPath: "/reset-password" }),
        },
      ),
    );
  },
  async resetPassword(token: string, password: string) {
    return data(
      await request<{ data: { message: string } }>("/auth/reset/confirm", {
        method: "POST",
        body: JSON.stringify({ token, new_password: password }),
      }),
    );
  },
  async me() {
    const payload = await request<{ user: User; data?: { user: User } }>(
      "/auth/me",
      {},
      true,
    );
    return data(payload).user;
  },
  logout() {
    setToken("");
  },
  async dashboard() {
    const payload = await request<{
      data: {
        profile: TalentProfile | null;
        settings: Settings;
        events: AgencyEvent[];
        registrations: EventRegistration[];
        notifications: TalentNotification[];
      };
    }>("/talent-platform/me", {}, true);
    return data(payload);
  },
  async talentAction(body: Record<string, unknown>) {
    const payload = await request<{
      data: {
        profile: TalentProfile | null;
        settings: Settings;
        events: AgencyEvent[];
        registrations: EventRegistration[];
        notifications: TalentNotification[];
      };
    }>(
      "/talent-platform/me",
      { method: "POST", body: JSON.stringify(body) },
      true,
    );
    return data(payload);
  },
  async saveProfile(profile: Partial<TalentProfile>) {
    const payload = await request<{ data: { profile: TalentProfile } }>(
      "/talent-platform/me",
      { method: "PUT", body: JSON.stringify(profile) },
      true,
    );
    return data(payload).profile;
  },
  async uploadProfilePhoto(input: {
    name: string;
    city: string;
    state: string;
    bio: string;
    public_slug: string;
    photo: File;
  }) {
    const form = new FormData();
    Object.entries(input).forEach(([key, value]) => form.set(key, value));
    form.set("public_enabled", "true");
    return request("/profile", { method: "PUT", body: form }, true);
  },
  async admin() {
    const payload = await request<{ data: AdminPlatformData }>(
      "/talent-platform/admin",
      {},
      true,
    );
    return data(payload);
  },
  async adminAction(body: Record<string, unknown>) {
    const payload = await request<{ data: AdminPlatformData }>(
      "/talent-platform/admin",
      { method: "POST", body: JSON.stringify(body) },
      true,
    );
    return data(payload);
  },
  async checkout(action: string, body: Record<string, unknown> = {}) {
    const payload = await request<{
      data: {
        url?: string;
        session_id?: string;
        active?: boolean;
        status?: string;
      };
    }>(
      "/talent-platform/checkout",
      { method: "POST", body: JSON.stringify({ action, ...body }) },
      true,
    );
    return data(payload);
  },
};
